<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  inboxes: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select']);

const selectedInboxId = ref(null);

// Group inboxes by type
const groupedInboxes = computed(() => {
  const result = [];
  const whatsappEvolution = [];
  const whatsappOfficial = [];
  const messengerInboxes = [];
  const otherChannels = [];

  props.inboxes.forEach(inbox => {
    const name = (inbox.inbox_name || inbox.name || '').toLowerCase();

    if (
      inbox.integration_type === 'whatsapp_cloud' ||
      inbox.integration_type === 'official' ||
      inbox.provider === 'whatsapp_cloud_api' ||
      name.includes('api oficial') ||
      name.includes('cloud') ||
      name.includes('official')
    ) {
      whatsappOfficial.push({ ...inbox, groupType: 'official' });
    } else if (
      inbox.integration_type === 'evolution' ||
      name.includes('evolution') ||
      name.includes('unofficial') ||
      (inbox.channel_type === 'whatsapp' && inbox.provider !== 'whatsapp_cloud_api')
    ) {
      whatsappEvolution.push({ ...inbox, groupType: 'evolution' });
    } else if (
      inbox.integration_type === 'messenger' ||
      inbox.channel_type === 'messenger' ||
      name.includes('messenger') ||
      name.includes('facebook')
    ) {
      messengerInboxes.push({ ...inbox, groupType: 'messenger' });
    } else {
      otherChannels.push({ ...inbox, groupType: 'other' });
    }
  });

  if (whatsappEvolution.length > 0) {
    result.push({ type: 'header', label: 'WhatsApp', icon: 'i-lucide-message-circle' });
    result.push(...whatsappEvolution);
  }

  if (whatsappOfficial.length > 0) {
    result.push({ type: 'header', label: 'WhatsApp API Oficial', icon: 'i-lucide-shield-check' });
    result.push(...whatsappOfficial);
  }

  if (messengerInboxes.length > 0) {
    result.push({ type: 'header', label: 'Messenger', icon: 'i-lucide-facebook' });
    result.push(...messengerInboxes);
  }

  if (otherChannels.length > 0) {
    result.push({ type: 'header', label: 'Outros Canais', icon: 'i-lucide-globe' });
    result.push(...otherChannels);
  }

  return result;
});

const hasConnectedInboxes = computed(() => props.inboxes.length > 0);

const canConfirm = computed(() => selectedInboxId.value !== null);

// Reset selection when modal opens
watch(
  () => props.show,
  newValue => {
    if (newValue) {
      selectedInboxId.value = null;
      // Pre-select first inbox if only one available
      if (props.inboxes.length === 1) {
        selectedInboxId.value = props.inboxes[0].id;
      }
    }
  }
);

const selectInbox = inboxId => {
  selectedInboxId.value = inboxId;
};

const confirmSelection = () => {
  if (selectedInboxId.value !== null) {
    emit('select', selectedInboxId.value);
  }
};

const getIntegrationBadge = inbox => {
  if (inbox.groupType === 'official') {
    return { label: 'Whatsapp API Oficial', class: 'bg-n-teal-3 text-n-teal-11 border-n-teal-6' };
  }
  if (inbox.groupType === 'evolution') {
    return { label: 'Whatsapp', class: 'bg-n-iris-3 text-n-iris-11 border-n-iris-6' };
  }
  if (inbox.groupType === 'messenger') {
    return { label: 'Messenger', class: 'bg-n-sky-3 text-n-sky-11 border-n-sky-6' };
  }
  return { label: 'Outro', class: 'bg-n-slate-3 text-n-slate-11 border-n-slate-6' };
};
</script>

<template>
  <transition name="modal-fade">
    <div
      v-if="show"
      class="absolute inset-0 z-40 flex items-center justify-center bg-n-slate-12/20 dark:bg-n-slate-1/70 p-4 overflow-y-auto"
    >
      <div
        class="bg-n-solid-1 dark:bg-n-solid-1 rounded-xl w-full max-w-lg shadow-xl border border-n-slate-6 dark:border-n-slate-6 my-8 max-h-[90vh] flex flex-col animate-in fade-in-0 zoom-in-95 duration-300"
      >
        <!-- Modal Header (no close button - mandatory selection) -->
        <div
          class="flex items-center p-6 border-b border-n-slate-6 dark:border-n-slate-6 bg-n-slate-2 dark:bg-n-slate-2 rounded-t-xl flex-shrink-0"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 bg-gradient-to-br from-primary-600 to-primary-800 dark:from-primary-600 dark:to-primary-800 rounded-lg flex items-center justify-center shadow-md"
            >
              <i class="i-lucide-inbox text-white text-xl" />
            </div>
            <div>
              <h3
                class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-12"
              >
                Selecione o Inbox
              </h3>
              <p class="text-sm text-n-slate-11">
                Escolha o inbox padrão para este disparo
              </p>
            </div>
          </div>
        </div>

        <!-- Modal Content -->
        <div class="p-6 flex-1 overflow-y-auto">
          <!-- Loading State -->
          <div
            v-if="loading"
            class="flex flex-col items-center justify-center py-12"
          >
            <i class="i-lucide-loader animate-spin text-3xl text-n-slate-9 mb-3" />
            <p class="text-n-slate-11">Carregando inboxes...</p>
          </div>

          <!-- No Connected Inboxes -->
          <div
            v-else-if="!hasConnectedInboxes"
            class="flex flex-col items-center justify-center py-12"
          >
            <div class="w-16 h-16 bg-n-amber-2 rounded-full flex items-center justify-center mb-4">
              <i class="i-lucide-wifi-off text-3xl text-n-amber-9" />
            </div>
            <h4 class="text-lg font-medium text-n-slate-12 mb-2">
              Nenhum inbox conectado
            </h4>
            <p class="text-n-slate-11 text-center max-w-sm">
              Conecte pelo menos um inbox para enviar mensagens em massa.
            </p>
          </div>

          <!-- Inbox List -->
          <div v-else class="space-y-2">
            <template v-for="(item, index) in groupedInboxes" :key="index">
              <!-- Group Header -->
              <div
                v-if="item.type === 'header'"
                class="flex items-center gap-2 pt-4 pb-2"
                :class="{ 'pt-0': index === 0 }"
              >
                <i :class="item.icon" class="text-n-slate-9" />
                <span class="text-xs font-semibold text-n-slate-9 uppercase tracking-wide">
                  {{ item.label }}
                </span>
              </div>

              <!-- Inbox Item -->
              <button
                v-else
                type="button"
                class="w-full p-4 rounded-lg border transition-all duration-200 flex items-center gap-4 text-left"
                :class="
                  selectedInboxId === item.id
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-n-slate-6 bg-n-solid-1 hover:border-n-slate-8 hover:bg-n-slate-2'
                "
                @click="selectInbox(item.id)"
              >
                <!-- Radio Circle -->
                <div
                  class="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors"
                  :class="
                    selectedInboxId === item.id
                      ? 'border-primary-500 bg-primary-500'
                      : 'border-n-slate-6'
                  "
                >
                  <i
                    v-if="selectedInboxId === item.id"
                    class="i-lucide-check text-white text-xs"
                  />
                </div>

                <!-- Inbox Info -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <span class="font-medium text-n-slate-12 truncate">
                      {{ item.inbox_name || item.name }}
                    </span>
                    <!-- Connection Status Dot -->
                    <span
                      class="w-2 h-2 rounded-full flex-shrink-0"
                      :class="item.isConnected ? 'bg-n-teal-9' : 'bg-n-ruby-9'"
                      :title="item.isConnected ? 'Conectado' : 'Desconectado'"
                    />
                  </div>
                  <div class="flex items-center gap-2 mt-1">
                    <!-- Integration Badge -->
                    <span
                      class="text-xs px-2 py-0.5 rounded-full border"
                      :class="getIntegrationBadge(item).class"
                    >
                      {{ getIntegrationBadge(item).label }}
                    </span>
                  </div>
                </div>
              </button>
            </template>
          </div>
        </div>

        <!-- Modal Footer -->
        <div
          class="flex gap-3 p-6 border-t border-n-slate-6 bg-n-slate-2 rounded-b-xl"
        >
          <button
            :disabled="!canConfirm"
            class="flex-1 px-4 py-3 bg-gradient-to-br from-primary-500 to-primary-600 dark:from-primary-500 dark:to-primary-600 hover:from-primary-600 hover:to-primary-700 dark:hover:from-primary-600 dark:hover:to-primary-700 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
            @click="confirmSelection"
          >
            <i class="i-lucide-check" />
            <span>Confirmar</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
